import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiInstagram, FiYoutube } = FiIcons;

const triggerAccessibility = () => {
  document.querySelector('.asw-menu-btn')?.click();
};

const legalLinks = [
  { name: 'Privacy Policy', href: '/privacy-policy' },
  { name: 'Terms of Service', href: '/terms-of-service' },
  { name: 'Acceptable Use', href: '/acceptable-use' },
  { name: 'Production Standards', href: '/production-standards' },
];

const getStudioStatus = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date());

  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  const minutes = Number(values.hour) * 60 + Number(values.minute);
  const isOpen = weekdays.includes(values.weekday) && minutes >= 600 && minutes < 1020;

  return isOpen;
};

function StudioStatus() {
  const [isOpen, setIsOpen] = useState(getStudioStatus);

  useEffect(() => {
    const updateStatus = () => setIsOpen(getStudioStatus());
    const interval = window.setInterval(updateStatus, 30000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div
      className="inline-flex items-center gap-2"
      aria-label={`Studio is currently ${isOpen ? 'open' : 'closed'}`}
      aria-live="polite"
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isOpen ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'
        }`}
        aria-hidden="true"
      />
      <span
        className={`text-[10px] font-mono font-black tracking-widest uppercase ${
          isOpen ? 'text-emerald-400' : 'text-red-400'
        }`}
      >
        {isOpen ? 'OPEN NOW' : 'CLOSED NOW'}
      </span>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#000000] text-[#FFFFFF] py-16 md:py-20 px-6 md:px-12 border-t border-neutral-900">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">

          {/* Column 1: Brand Lockup, Address & Contact */}
          <div>
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-black tracking-tighter text-white uppercase leading-none">
                BAY AREA
              </span>
              <span className="text-xs font-bold tracking-[0.25em] text-white uppercase mt-1">
                PODCAST STUDIO
              </span>
            </div>

            <div className="mt-7">
              <p className="text-[10px] font-mono font-black tracking-widest text-neutral-400 uppercase mb-2">
                STUDIO LOCATION
              </p>
              <div className="space-y-1 text-xs text-white font-medium">
                <p>3909 Washington Blvd, Ste 206</p>
                <p>Fremont, CA 94538</p>
              </div>
            </div>

            <div className="mt-5">
              <p className="text-[10px] font-mono font-black tracking-widest text-neutral-400 uppercase mb-2">
                CONTACT
              </p>
              <div className="flex flex-col items-start space-y-1 text-xs text-white font-bold">
                <a
                  href="mailto:info@bayareapodcast.studio"
                  className="hover:text-neutral-300 transition-colors duration-150 ease-in-out"
                >
                  info@bayareapodcast.studio
                </a>
                <a
                  href="tel:+15104971649"
                  className="hover:text-neutral-300 transition-colors duration-150 ease-in-out"
                >
                  (510) 497-1649
                </a>
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-2">
                <StudioStatus />
              </div>
              <div className="text-xs text-white font-medium space-y-1">
                <p>Monday - Friday</p>
                <p>10 AM - 5 PM</p>
              </div>
            </div>

            <div className="mt-6 flex items-center space-x-4">
              <a
                href="https://www.instagram.com/bayareapodcaststudio/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-neutral-400 transition-colors duration-150 ease-in-out"
                aria-label="Instagram"
              >
                <SafeIcon icon={FiInstagram} className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@BayAreaPodcastStudio"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-neutral-400 transition-colors duration-150 ease-in-out"
                aria-label="YouTube"
              >
                <SafeIcon icon={FiYoutube} className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h3 className="text-xs font-mono font-black tracking-widest text-neutral-400 uppercase mb-4">
              NAVIGATION
            </h3>
            <ul className="flex flex-col space-y-3 font-medium">
              {[
                { name: 'Studio Sessions', href: '/studio-sessions' },
                { name: 'Video Services', href: '/video-services' },
                { name: 'Portfolio', href: '/portfolio' },
                { name: 'About', href: '/about' },
                { name: 'FAQ', href: '/faq' },
                { name: 'Lookbook', href: '/lookbook' },
                { name: 'Mobile Podcast Shoot', href: '/mobile-podcast' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-xs text-white hover:text-neutral-300 transition-colors duration-150 ease-in-out block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div>
            <h3 className="text-xs font-mono font-black tracking-widest text-neutral-400 uppercase mb-4">
              LEGAL
            </h3>
            <ul className="flex flex-col space-y-3 font-medium">
              {legalLinks.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-xs text-white hover:text-neutral-300 transition-colors duration-150 ease-in-out block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  data-gt-cookie-widget-show="true"
                  className="text-xs text-white hover:text-neutral-300 transition-colors duration-150 ease-in-out block text-left focus:outline-none"
                >
                  Manage Cookie Preferences
                </button>
              </li>
              <li>
                <button
                  onClick={triggerAccessibility}
                  aria-label="Open accessibility options"
                  className="text-xs text-white hover:text-neutral-300 transition-colors duration-150 ease-in-out block text-left focus:outline-none"
                >
                  Accessibility
                </button>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Sub-Footer */}
        <div className="border-t border-neutral-900 pt-8 mt-16 flex flex-col md:flex-row justify-between items-center text-[11px] font-mono text-neutral-500 gap-4 text-center md:text-left">
          <p>© {year} Claxxiq LLC. All rights reserved.</p>
          <p>BAY AREA // SILICON VALLEY</p>
        </div>

      </div>
    </footer>
  );
}